import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useCallback, useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { ColorHex } from 'src/types/utilityTypes';

import { Box } from './Box';
import { Button } from './Button';
import { DropdownMenu } from './DropdownMenu';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { Stack } from './Stack';
import { TextField } from './TextField';
import { Typography } from './Typography';

interface ColorPickerProps {
  color?: ColorHex;
  showHex?: boolean;
  onChange?: (color: string) => void;
  label?: string;
  description?: string;
  allowClear?: boolean;
  disabled?: boolean;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  color = '#FFFFFF',
  showHex = false,
  onChange,
  label,
  description,
  allowClear = true,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleChange = (color: string) => {
    onChange?.(color);
  };

  const handleClear = () => {
    onChange?.('');
  };

  return (
    <>
      <DropdownMenu
        open={open}
        setOpen={setOpen}
        disabled={disabled}
        trigger={
          <Stack spacing={2}>
            {label && <Typography>{label}</Typography>}
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {description}
              </Typography>
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  padding: 0,
                  borderRadius: 1,
                  backgroundColor: color || '#FFFFFF',
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  cursor: disabled ? 'default' : 'pointer',
                  '&:hover': !disabled
                    ? {
                        backgroundColor: color || '#FFFFFF',
                        opacity: 0.8,
                      }
                    : {
                        backgroundColor: color || '#FFFFFF',
                      },
                }}
                disabled={disabled}
              />
              {showHex && <Typography variant="body2">{color}</Typography>}
              {allowClear && color && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                >
                  <Icon
                    size="xsmall"
                    icon={faXmark}
                  />
                </IconButton>
              )}
            </Stack>
          </Stack>
        }
        closeOnContentClick={false}
      >
        <CustomColorPicker
          color={color}
          onChange={handleChange}
          setOpen={setOpen}
        />
      </DropdownMenu>
    </>
  );
};

const CustomColorPicker: React.FC<{
  color: string;
  onChange: (color: string) => void;
  setOpen: (open: boolean) => void;
}> = ({ color, onChange }) => {
  const [inputValue, setInputValue] = useState(color);

  // Update input when color changes from picker
  useEffect(() => {
    setInputValue(color);
  }, [color]);

  const handleHexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);

      // Only update if it's a valid hex color
      if (/^#[0-9A-F]{6}$/i.test(value)) {
        onChange(value);
      }
    },
    [onChange]
  );

  return (
    <Box
      sx={{
        padding: 2,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 24,
      }}
    >
      {/* Color Picker with default styling */}
      <Box sx={{ marginBottom: 2 }}>
        <HexColorPicker color={color} onChange={onChange} />
      </Box>

      {/* Hex Input + Presets Row */}
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Color Preview Circle */}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: color,
            border: (theme) => `1px solid ${theme.palette.grey[300]}`,
            flexShrink: 0,
          }}
        />

        <TextField
          value={inputValue}
          onChange={handleHexChange}
          placeholder="#FFFFFF"
          size="small"
          sx={{ flexGrow: 1, minWidth: 100 }}
        />
      </Stack>
    </Box>
  );
};
